import React, { useState } from 'react';
import { Plus, Trash2, Upload, Link, Play, ExternalLink, Image, Film, Monitor } from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { profileService } from '../../../services/profileService';

const DemoFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const [activeDemo, setActiveDemo] = useState(null);

  const handleDemoUpload = async (file, error, demoIndex) => {
    if (error) return;
    setUploadStates(prev => ({ ...prev, [demoIndex]: true }));
    
    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'demo', demoIndex);
      const demos = [...(formData.demos || [])];
      demos[demoIndex] = {
        ...demos[demoIndex],
        url: result.url,
        type: file.type.startsWith('video/') ? 'video' : 'image',
        fileName: result.fileName
      };
      updateFormData('demos', demos);
    } catch (error) {
      console.error('Error uploading demo:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, [demoIndex]: false }));
    }
  };

  const handleMultipleImageUpload = async (files, error, demoIndex) => {
    if (error || !files?.length) return;
    setUploadStates(prev => ({ ...prev, [`${demoIndex}_images`]: true }));
    
    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const result = await profileService.uploadProfileMedia(file, bookId, 'demo', `${demoIndex}_img_${index}`);
        return {
          url: result.url,
          description: '',
          fileName: result.fileName
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      const demos = [...(formData.demos || [])];
      demos[demoIndex] = {
        ...demos[demoIndex],
        images: [...(demos[demoIndex].images || []), ...uploadedImages]
      };
      updateFormData('demos', demos);
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, [`${demoIndex}_images`]: false }));
    }
  };

  const extractVideoId = (url, platform) => {
    if (!url) return null;
    
    switch (platform) {
      case 'youtube':
        const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
        return youtubeMatch ? youtubeMatch[1] : null;
      case 'vimeo':
        const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
        return vimeoMatch ? vimeoMatch[1] : null;
      case 'loom':
        const loomMatch = url.match(/(?:loom\.com\/share\/)([a-f0-9]+)/);
        return loomMatch ? loomMatch[1] : null;
      default:
        return null;
    }
  };

  const getEmbedUrl = (url, platform) => {
    const videoId = extractVideoId(url, platform);
    if (!videoId) return null;

    switch (platform) {
      case 'youtube':
        return `https://www.youtube.com/embed/${videoId}`;
      case 'vimeo':
        return `https://player.vimeo.com/video/${videoId}`;
      case 'loom':
        return `https://www.loom.com/embed/${videoId}`;
      default:
        return null;
    }
  };

  const addDemo = () => {
    const demos = formData.demos || [];
    demos.push({
      id: Date.now(),
      title: '',
      description: '',
      type: 'interactive', // Default to interactive
      demoUrl: '',
      videoUrl: '',
      videoPlatform: 'youtube',
      url: '',
      images: [],
      embedSettings: {
        allowFullscreen: true,
        autoplay: false,
        showControls: true
      }
    });
    updateFormData('demos', demos);
  };

  const updateDemo = (index, field, value) => {
    const demos = [...(formData.demos || [])];
    demos[index] = { ...demos[index], [field]: value };
    updateFormData('demos', demos);
  };

  const updateDemoImage = (demoIndex, imageIndex, field, value) => {
    const demos = [...(formData.demos || [])];
    demos[demoIndex].images[imageIndex] = { ...demos[demoIndex].images[imageIndex], [field]: value };
    updateFormData('demos', demos);
  };

  const removeDemo = (index) => {
    const demos = (formData.demos || []).filter((_, i) => i !== index);
    updateFormData('demos', demos);
  };

  const removeImage = (demoIndex, imageIndex) => {
    const demos = [...(formData.demos || [])];
    demos[demoIndex].images = demos[demoIndex].images.filter((_, i) => i !== imageIndex);
    updateFormData('demos', demos);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Product Demos & Interactive Content
        </label>
        <button
          type="button"
          onClick={addDemo}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Demo
        </button>
      </div>

      {(formData.demos || []).map((demo, idx) => (
        <div key={demo.id || idx} className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
          {/* Demo Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <input
                type="text"
                value={demo.title}
                onChange={(e) => updateDemo(idx, 'title', e.target.value)}
                className="text-lg font-semibold bg-transparent border-none outline-none focus:ring-0 p-0 w-full"
                placeholder="Demo title"
              />
            </div>
            <button
              type="button"
              onClick={() => removeDemo(idx)}
              className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 transition-colors"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>

          {/* Demo Type Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <button
              type="button"
              onClick={() => updateDemo(idx, 'type', 'interactive')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                demo.type === 'interactive' 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Monitor className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Interactive Demo</div>
                <div className="text-xs text-gray-500">Live web demo</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => updateDemo(idx, 'type', 'video')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                demo.type === 'video' 
                  ? 'border-purple-500 bg-purple-50 text-purple-700' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Film className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Video Demo</div>
                <div className="text-xs text-gray-500">YouTube, Vimeo, Loom</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => updateDemo(idx, 'type', 'gallery')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                demo.type === 'gallery' 
                  ? 'border-green-500 bg-green-50 text-green-700' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Image className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Image Gallery</div>
                <div className="text-xs text-gray-500">Screenshots</div>
              </div>
            </button>
          </div>

          {/* Demo Description */}
          <textarea
            value={demo.description}
            onChange={(e) => updateDemo(idx, 'description', e.target.value)}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-6 resize-none"
            placeholder="Demo description..."
          />

          {/* Interactive Demo Fields */}
          {demo.type === 'interactive' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ExternalLink className="h-4 w-4 inline mr-2" />
                  Live Demo URL
                </label>
                <input
                  type="url"
                  value={demo.demoUrl}
                  onChange={(e) => updateDemo(idx, 'demoUrl', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://your-demo.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={demo.embedSettings?.allowFullscreen !== false}
                    onChange={(e) => updateDemo(idx, 'embedSettings', { 
                      ...demo.embedSettings, 
                      allowFullscreen: e.target.checked 
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">Allow Fullscreen</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={demo.embedSettings?.autoplay || false}
                    onChange={(e) => updateDemo(idx, 'embedSettings', { 
                      ...demo.embedSettings, 
                      autoplay: e.target.checked 
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">Autoplay</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={demo.embedSettings?.showControls !== false}
                    onChange={(e) => updateDemo(idx, 'embedSettings', { 
                      ...demo.embedSettings, 
                      showControls: e.target.checked 
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">Show Controls</span>
                </label>
              </div>

              {/* Interactive Demo Preview */}
              {demo.demoUrl && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setActiveDemo(activeDemo === idx ? null : idx)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mb-3"
                  >
                    <Play className="h-4 w-4" />
                    {activeDemo === idx ? 'Hide Preview' : 'Show Preview'}
                  </button>
                  
                  {activeDemo === idx && (
                    <div className="bg-gray-900 rounded-lg overflow-hidden">
                      <iframe
                        src={demo.demoUrl}
                        className="w-full aspect-video"
                        frameBorder="0"
                        allowFullScreen={demo.embedSettings?.allowFullscreen !== false}
                        title={demo.title}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Video Demo Fields */}
          {demo.type === 'video' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <select
                  value={demo.videoPlatform || 'youtube'}
                  onChange={(e) => updateDemo(idx, 'videoPlatform', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                  <option value="loom">Loom</option>
                  <option value="upload">Upload Video</option>
                </select>
                
                {demo.videoPlatform !== 'upload' && (
                  <input
                    type="url"
                    value={demo.videoUrl}
                    onChange={(e) => updateDemo(idx, 'videoUrl', e.target.value)}
                    className="md:col-span-3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={`${demo.videoPlatform === 'youtube' ? 'YouTube' : demo.videoPlatform === 'vimeo' ? 'Vimeo' : 'Loom'} video URL`}
                  />
                )}
              </div>

              {demo.videoPlatform === 'upload' && (
                <FileUpload
                  label="Upload Video"
                  currentFile={demo.url}
                  onFileSelect={(file, error) => handleDemoUpload(file, error, idx)}
                  onRemove={() => updateDemo(idx, 'url', '')}
                  accept="video/*"
                  maxSize={100}
                  uploading={uploadStates[idx]}
                  showPreview={true}
                />
              )}

              {/* Video Preview */}
              {(demo.videoUrl || demo.url) && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setActiveDemo(activeDemo === idx ? null : idx)}
                    className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 mb-3"
                  >
                    <Play className="h-4 w-4" />
                    {activeDemo === idx ? 'Hide Preview' : 'Show Preview'}
                  </button>
                  
                  {activeDemo === idx && (
                    <div className="bg-gray-900 rounded-lg overflow-hidden">
                      {demo.videoPlatform === 'upload' ? (
                        <video
                          src={demo.url}
                          controls
                          className="w-full aspect-video"
                        />
                      ) : (
                        <iframe
                          src={getEmbedUrl(demo.videoUrl, demo.videoPlatform)}
                          className="w-full aspect-video"
                          frameBorder="0"
                          allowFullScreen
                          title={demo.title}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Image Gallery Fields */}
          {demo.type === 'gallery' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Image className="h-4 w-4 inline mr-2" />
                  Upload Images/Screenshots
                </label>
                <FileUpload
                  label="Select Multiple Images"
                  onFileSelect={(files, error) => handleMultipleImageUpload(files, error, idx)}
                  accept="image/*"
                  multiple={true}
                  maxSize={10}
                  uploading={uploadStates[`${idx}_images`]}
                  showPreview={false}
                />
              </div>

              {/* Image Grid */}
              {demo.images && demo.images.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {demo.images.map((image, imgIdx) => (
                    <div key={imgIdx} className="relative group">
                      <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={image.url}
                          alt={image.description || `Screenshot ${imgIdx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => removeImage(idx, imgIdx)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                      
                      <textarea
                        value={image.description || ''}
                        onChange={(e) => updateDemoImage(idx, imgIdx, 'description', e.target.value)}
                        rows="2"
                        className="w-full mt-2 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 resize-none"
                        placeholder="Image description..."
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {(!formData.demos || formData.demos.length === 0) && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl">
          <Monitor className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No demos added yet</h3>
          <p className="text-sm text-gray-500 mb-4">Add interactive demos, videos, or image galleries to showcase your product.</p>
          <button
            type="button"
            onClick={addDemo}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Your First Demo
          </button>
        </div>
      )}
    </div>
  );
};

export default DemoFormFields;